import React, { useMemo, useState } from "react";
import { Button, Icon, Modal, Notification, TextField } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import { isTeliaSkyService } from "../../../../../constants/app";
import { RootState } from "../../../../../models";
import { getFlattenedCartContent } from "../../../checkout/state/selectors/cartSelectors";
import { getSubscriptions } from "../../../checkout/state/selectors/subscriptionsSelectors";
import { updateSubscriptionProductUserEmail } from "../../state/actions";
import TEXT from "../../text-consts";
import { hasDisplayableMessage } from "../../utils";
import "./MissingTeliaSkyEmailModal.less";

export interface MissingTeliaSkyEmailUser {
    name: string;
    number: string;
    subscriptionId: string;
}

interface MissingTeliaSkyEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const isValidEmail = (value: string): boolean => {
    const trimmed = value.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

const MissingTeliaSkyEmailModal = ({ isOpen, onClose }: MissingTeliaSkyEmailModalProps) => {
    const dispatch = useDispatch();
    const subscriptions = useSelector((state: RootState) => getSubscriptions(state));
    const flattenedCartContent = useSelector((state: RootState) => getFlattenedCartContent(state));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const users = useMemo<MissingTeliaSkyEmailUser[]>(() => {
        return Object.entries(subscriptions).flatMap(([subscriptionKey, subscription]) => {
            const subscriptionId = subscription.id || subscriptionKey;
            const hasEmail = Boolean(subscription.productUser?.email?.trim());

            if (!subscriptionId || hasEmail) {
                return [];
            }

            const subscriptionCartItem = flattenedCartContent.find((item) => item.bundleId === subscriptionId);
            const hasTeliaSky = subscriptionCartItem?.items?.some((item) => isTeliaSkyService(item.id));

            if (!hasTeliaSky) {
                return [];
            }

            const name =
                `${subscription.productUser?.firstName || ""} ${subscription.productUser?.lastName || ""}`.trim();

            const selectedPhoneNumber = subscription.phoneNumber?.selectedPhoneNumber;

            return [
                {
                    subscriptionId,
                    name: name || "Ukjent bruker",
                    number: selectedPhoneNumber || "Ukjent nummer",
                },
            ];
        });
    }, [flattenedCartContent, subscriptions]);

    const [emails, setEmails] = useState<Record<string, string>>(() =>
        users.reduce<Record<string, string>>((acc, user) => {
            acc[user.subscriptionId] = "";
            return acc;
        }, {}),
    );

    const isConfirmDisabled = useMemo(
        () =>
            users.some((user) => {
                const value = emails[user.subscriptionId]?.trim() || "";
                return !isValidEmail(value);
            }),
        [emails, users],
    );

    const handleConfirm = async () => {
        const nextErrors: Record<string, string> = {};

        users.forEach((user) => {
            const value = emails[user.subscriptionId]?.trim() || "";
            if (!value) {
                nextErrors[user.subscriptionId] = "E-post er påkrevd";
                return;
            }

            if (!isValidEmail(value)) {
                nextErrors[user.subscriptionId] = "Ugyldig e-postadresse";
            }
        });

        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            return;
        }

        setSubmitError(null);
        setFieldErrors({});
        setIsSubmitting(true);

        try {
            const entries = users.map((user) => ({
                subscriptionId: user.subscriptionId,
                email: (emails[user.subscriptionId] || "").trim(),
            }));

            for (const entry of entries) {
                await dispatch(updateSubscriptionProductUserEmail(entry.subscriptionId, entry.email));
            }

            window.location.reload();
        } catch (e) {
            setSubmitError(
                hasDisplayableMessage(e)
                    ? e.displayableMessage
                    : e instanceof Error
                      ? e.message
                      : TEXT.ERRORS.SUBMIT_ERROR,
            );
        }
    };

    if (!isOpen || users.length === 0) {
        return null;
    }

    return (
        <Modal open onOpenChange={(open) => (!open ? onClose() : undefined)}>
            <Modal.Overlay>
                <Modal.Content>
                    <Modal.Group variant="title">
                        <Modal.Title as="h3">Legg til e-post</Modal.Title>
                        <Modal.Close aria-label="lukk" slot="floating" onClick={onClose} />
                    </Modal.Group>

                    <p className="missing-telia-sky-email-modal__text">
                        Telia Sky krever at bruker har en e-postadresse.
                    </p>

                    <div className="missing-telia-sky-email-modal__rows">
                        {users.map((user) => (
                            <div className="missing-telia-sky-email-modal__row" key={user.subscriptionId}>
                                <div className="missing-telia-sky-email-modal__label">
                                    <Icon name="end-user" size="sm" aria-hidden="true" focusable="false" />
                                    <span>{user.name}</span>
                                    <span>{user.number}</span>
                                </div>
                                <TextField>
                                    <TextField.Input
                                        type="email"
                                        aria-label={`E-postadresse for ${user.name}`}
                                        value={emails[user.subscriptionId] || ""}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            const value = event.currentTarget.value;
                                            setEmails((prev) => ({
                                                ...prev,
                                                [user.subscriptionId]: value,
                                            }));
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                [user.subscriptionId]: "",
                                            }));
                                        }}
                                        placeholder="Skriv inn e-post"
                                    />
                                </TextField>
                                {fieldErrors[user.subscriptionId] && (
                                    <p className="missing-telia-sky-email-modal__error">
                                        {fieldErrors[user.subscriptionId]}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>

                    {submitError && (
                        <Notification type="tile" variant="error" mt="100">
                            <Notification.Icon />
                            <Notification.Text>{submitError}</Notification.Text>
                        </Notification>
                    )}

                    <Modal.Group variant="action">
                        <Modal.Close slot="content">
                            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                                Avbryt
                            </Button>
                        </Modal.Close>
                        <Button
                            onClick={handleConfirm}
                            loading={isSubmitting}
                            disabled={isSubmitting || isConfirmDisabled}
                        >
                            Legg til e-post
                        </Button>
                    </Modal.Group>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default MissingTeliaSkyEmailModal;
